import { useEffect, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-geometryutil";
import "leaflet-draw/dist/leaflet.draw.css";

const AoiControl = (props) => {
  const map = props.map;
  const [activeTool, setActiveTool] = useState(null);
  const [layerGroup, setLayerGroup] = useState(null);
  const [drawHandler, setDrawHandler] = useState(null);
  const [areaValue, setAreaValue] = useState(null);
  const [isDownloading, setIsDownloading] = useState(null);
  const [position, setPosition] = useState({
    topLeftLat: "",
    topLeftLng: "",
    topRightLat: "",
    topRightLng: "",
    bottomLeftLat: "",
    bottomLeftLng: "",
    bottomRightLat: "",
    bottomRightLng: "",
  });

  const stopMeasurement = useCallback(() => {
    map.off("click");
    map.off("mousemove");
    map.off(L.Draw.Event.CREATED);

    if (drawHandler) drawHandler.disable();
    setDrawHandler(null);

    if (layerGroup) layerGroup.clearLayers();

    setActiveTool(null);
    setAreaValue(null);

    const tooltipEl = document.querySelector(".leaflet-draw-tooltip");
    if (tooltipEl) {
      tooltipEl.style.display = "none";
      tooltipEl.style.left = "0px";
      tooltipEl.style.top = "0px";
    }
  }, [map, drawHandler, layerGroup]);

  useEffect(() => {
    if (props.activeWidget !== "AOI") {
      stopMeasurement();
    }
  }, [props.activeWidget, stopMeasurement]);

  useEffect(() => {
    if (props.activeWidget === "AOI") {
      const panel = document.querySelector(".widget-panel-right");
      const buttons = document.querySelector(".buttons-container-right");
      if (panel && props.isMobile) {
        if (drawHandler) {
          panel.style.display = "none";
          buttons.style.display = "";
        } else {
          panel.style.display = "";
          buttons.style.display = "none";
        }
      }
    } 
  }, [drawHandler, props.activeWidget, props.isMobile]);

  useEffect(() => {
    if (!map) return;

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    setLayerGroup(drawnItems);

    const draw = new L.Control.Draw({
      draw: {
        rectangle: true,
        polygon: false,
        polyline: false,
        circle: false,
        marker: false,
      },
      edit: {
        featureGroup: drawnItems,
        edit: false,
        remove: false,
      }
    });

    map.addControl(draw);

    return () => {
      map.removeControl(draw);
      map.removeLayer(drawnItems);
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;

    const container = map.getContainer();
    container.style.cursor = activeTool ? "crosshair" : "";

    return () => {
      container.style.cursor = "";
    };
  }, [activeTool, map]);

  const getArea = () => {
    stopMeasurement();
    setActiveTool(true);

    const handler = new L.Draw.Rectangle(map, {
      shapeOptions: { color: "#615C9F", opacity: 0.8 },
    });

    setDrawHandler(handler);
    handler.enable();

    map.once(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      layerGroup.addLayer(layer);
      const latlngs = layer.getLatLngs()[0];

      const topLeft = latlngs[0];
      const topRight = latlngs[1];
      const bottomLeft = latlngs[2];
      const bottomRight = latlngs[3];

      setPosition({
        topLeftLat: topLeft.lat.toFixed(4),
        topLeftLng: topLeft.lng.toFixed(4),

        topRightLat: topRight.lat.toFixed(4),
        topRightLng: topRight.lng.toFixed(4),

        bottomLeftLat: bottomLeft.lat.toFixed(4),
        bottomLeftLng: bottomLeft.lng.toFixed(4),

        bottomRightLat: bottomRight.lat.toFixed(4),
        bottomRightLng: bottomRight.lng.toFixed(4),
      });

      const area = L.GeometryUtil.geodesicArea(latlngs);
      setAreaValue((area / 1000000).toFixed(2));
      setDrawHandler(null);
      stopDrawing();
    });
  };

  const stopDrawing = () => {
    map.off("click");
    map.off("mousemove");
    map.off(L.Draw.Event.CREATED);

    if (drawHandler) drawHandler.disable();
    setDrawHandler(null);

    if (map) {
      const container = map.getContainer();
      container.style.cursor = "";
    }
  };

  const downloadPolygon = () => {
    setIsDownloading(true);

    const layer = layerGroup.getLayers()[0];
    if (!layer) {
      alert("No polygon to download");
      setIsDownloading(false);
      return;
    }

    const latlngs = layer.getLatLngs()[0];

    const topLeft = latlngs[0];
    const topRight = latlngs[1];
    const bottomLeft = latlngs[2];
    const bottomRight = latlngs[3];

    const kml =
      `<?xml version="1.0" encoding="UTF-8"?>
        <kml xmlns="http://www.opengis.net/kml/2.2">
          <Document>
            <Placemark>
              <name>Area of Interest</name>
              <Polygon>
                <outerBoundaryIs>
                  <LinearRing>
                    <coordinates>
                      ${topLeft.lng},${topLeft.lat},0
                      ${bottomRight.lng},${bottomRight.lat},0
                      ${bottomLeft.lng},${bottomLeft.lat},0
                      ${topRight.lng},${topRight.lat},0
                      ${topLeft.lng},${topLeft.lat},0  <!-- Cerrar el polígono -->
                    </coordinates>
                  </LinearRing>
                </outerBoundaryIs>
              </Polygon>
            </Placemark>
          </Document>
        </kml>`;

    const blob = new Blob([kml], { type: "application/vnd.google-earth.kml+xml" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "AOI.kml";
    link.click();

    setIsDownloading(false);
  };

  return (
    <>
      {props.activeWidget === "AOI" && (
        <div className="aoi-container">
          <div className="widget-text">
            Set an area of interest and download the polygon
          </div>
          {activeTool ? (
            <button className="ccl-button ccl-button--default ccl-button--default-neg" onClick={stopMeasurement} aria-label={areaValue ? "Clear" : "Stop"}>
              {areaValue ? "Clear" : "Stop"}
            </button>
          ) : (
            <button className="ccl-button ccl-button--default" onClick={() => getArea()} aria-label="Start">
              Start
            </button>
          )}
          {areaValue && (
            <>
              <hr />
              <div className="widget-subtitle">Area of interest coordinates</div>
              <div className="aoi-coords">
                <div className="aoi-top">
                  <div className="widget-input disabled">
                    <i className="fa-solid fa-n"></i>
                    <input
                      type="text"
                      className="ccl-text-input"
                      value={Math.max(position.topLeftLat, position.topRightLat, position.bottomLeftLat, position.bottomRightLat)}
                      placeholder="Top Left Lat"
                      disabled
                    />
                  </div>
                </div>
                <div className="aoi-middle">
                  <div className="widget-input disabled">
                    <i className="fa-solid fa-w"></i>
                    <input
                      type="text"
                      className="ccl-text-input"
                      value={Math.min(position.topLeftLng, position.topRightLng, position.bottomLeftLng, position.bottomRightLng)}
                      placeholder="Top Right Lng"
                      disabled
                    />
                  </div>
                  <div className="widget-input disabled">
                    <i className="fa-solid fa-e"></i>
                    <input
                      type="text"
                      className="ccl-text-input"
                      value={Math.max(position.topLeftLng, position.topRightLng, position.bottomLeftLng, position.bottomRightLng)}
                      placeholder="Top Right Lat"
                      disabled
                    />
                  </div>
                </div>
                <div className="aoi-bottom">
                  <div className="widget-input disabled">
                    <i className="fa-solid fa-s"></i>
                    <input
                      type="text"
                      className="ccl-text-input"
                      value={Math.min(position.topLeftLat, position.topRightLat, position.bottomLeftLat, position.bottomRightLat)}
                      placeholder="Top Left Lng"
                      disabled
                    />
                  </div>
                </div>
              </div>
              <div className="measurement-display">Area: {areaValue} km²</div>
              <button className="ccl-button ccl-button--default ccl-button--default-neg" disabled={isDownloading} onClick={downloadPolygon} aria-label="Download">
                {isDownloading ? <span><i className="fas fa-spinner fa-pulse"></i>Downloading</span> : "Download"}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default AoiControl;
